import { Col, Row, Spin, DatePicker } from "antd";
import ReactApexChart from "react-apexcharts";
import { useEffect, useState } from "react";
import { getDashboardApi } from "mock/dashboardDemoData";
import moment from "moment";
import { managerQuery } from "./managerScope";

const CleaningAuditChart = ({ isLaundry }) => {
    const [cleaningAuditStatusData, setCleaningAuditStatusData] = useState(null);
    const [laundryAuditStatusData, setLaundryAuditStatusData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(moment());
    const clientId = localStorage.getItem("client_id");

    useEffect(() => {
        let isMounted = true;
        
        const fetchData = async () => {
            if (isMounted) setIsLoading(true);
            try {
                const dateParam = selectedDate.format('YYYY-MM');
                if (isLaundry) {
                    const { error, result } = await getDashboardApi(`/client_laundry_audit_report?client_id=${clientId}&date=${dateParam}${managerQuery()}`);
                    if (isMounted) setLaundryAuditStatusData(!error ? { result: result?.result } : null);
                } else {
                    const { error, result } = await getDashboardApi(`/client_cleaning_audit_report?client_id=${clientId}&date=${dateParam}${managerQuery()}`);
                    if (isMounted) setCleaningAuditStatusData(!error ? { result: result?.result } : null);
                }
            } catch (err) {
                console.error('Error fetching audit data:', err);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        
        if (clientId) {
            fetchData();
        } else {
            setIsLoading(false);
        }
        
        return () => {
            isMounted = false;
        };
    }, [clientId, isLaundry, selectedDate]);

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
        }
    };

    const totalCleaningAudit = Number(cleaningAuditStatusData?.result?.[0]?.total_facilities || 0)
    const completedCleaningAudit = Number(cleaningAuditStatusData?.result?.[0]?.completed || 0)
    const outstandingCleaningAudit = Number(cleaningAuditStatusData?.result?.[0]?.pending || 0)
    const completedCleaningAuditCorrectiveAction = Number(cleaningAuditStatusData?.result?.[0]?.rectification_completed || 0)
    const outstandingCleaningAuditCorrectiveAction = Number(cleaningAuditStatusData?.result?.[0]?.rectification_needed || 0)
    const totalCleaningAuditCorrectiveAction = outstandingCleaningAuditCorrectiveAction + completedCleaningAuditCorrectiveAction;
    
    const totalLaundryAudit = Number(laundryAuditStatusData?.result?.[0]?.total_facilities || 0)
    const completedLaundryAudit = Number(laundryAuditStatusData?.result?.[0]?.audit_completed || 0)
    const outstandingLaundryAudit = Number(laundryAuditStatusData?.result?.[0]?.audit_pending || 0)
    const completedLaundryAuditCorrectiveAction = Number(laundryAuditStatusData?.result?.[0]?.rectification_completed || 0)
    const outstandingLaundryAuditCorrectiveAction = Number(laundryAuditStatusData?.result?.[0]?.rectification_pending || 0)
    const totalLaundryAuditCorrectiveAction = outstandingLaundryAuditCorrectiveAction + completedLaundryAuditCorrectiveAction;
    
    const totalAudit = isLaundry ? totalLaundryAudit : totalCleaningAudit;
    const completedAudit = isLaundry ? completedLaundryAudit : completedCleaningAudit;
    const outstandingAudit = isLaundry ? outstandingLaundryAudit : outstandingCleaningAudit;

    // Nothing to count: an empty grey ring (ApexCharts draws nothing for all zeros)
    const isEmptyChart = Number(completedAudit) + Number(outstandingAudit) === 0;
    const chartOptions = {
        chart: {
            type: "donut",
        },
        labels: isEmptyChart ? [""] : ["Completed", "Outstanding"],
        colors: isEmptyChart ? ["#E4E7EC"] : ["#1A8F5C", "#B4690E"], // Completed, Outstanding: same as the legend squares (index.css)
        tooltip: { enabled: !isEmptyChart },
        states: isEmptyChart ? { hover: { filter: { type: "none" } }, active: { filter: { type: "none" } } } : {},
        legend: {
            show: false,
        },
        dataLabels: {
            enabled: false,
        },
        plotOptions: {
            pie: {
                donut: {
                    size: "70%",
                    labels: {
                        show: true,
                        name: {
                            show: false,
                        },
                        value: {
                            show: true,
                            fontSize: "24px",
                            fontWeight: 700,
                            color: "#101828",
                            formatter: () => totalAudit,
                        },
                        total: {
                            show: true,
                            label: "",
                            fontSize: "24px",
                            fontWeight: 700,
                            color: "#101828",
                            formatter: () => totalAudit,
                        },
                    },
                },
            },
        },
        stroke: {
            width: 0,
        },
    };

    const auditData = [completedAudit, outstandingAudit];

    return (
        <div className="audit-status-container">
            <div className="audit-status-header">
                <h3>{isLaundry ? "Laundry" : "Cleaning"} Audit Status</h3>
                <p>Monthly view</p>
                <DatePicker 
                    picker="month" 
                    value={selectedDate}
                    onChange={handleDateChange}
                    format="MM-YYYY"
                    placeholder="Select Month"
                    style={{ width: 120, fontSize: '12px' }}
                    allowClear={false}
                    disabledDate={(current) => current && current > moment().endOf('month')}
                    size="small"
                />
            </div>
            
            {isLoading ? (
                <div style={{ height: "200px", width: "100%" }} className="d-flex justify-content-center align-items-center">
                    <Spin />
                </div>
            ) : (
                <>
                    {/* Row 1: Chart and Legend */}
                    <Row gutter={20} className="audit-chart-row" align="middle">
                        <Col span={12}>
                            <div className="audit-chart-container">
                                <ReactApexChart 
                                    options={chartOptions} 
                                    series={isEmptyChart ? [1] : auditData} 
                                    type="donut" 
                                    width="140" 
                                    height={140}
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="audit-legend">
                                <div className="legend-item">
                                    <div className="legend-color audit-completed"></div>
                                    <span>Completed</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color audit-outstanding"></div>
                                    <span>Outstanding</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    
                    {/* Row 2: Stats */}
                    <Row gutter={20} className="audit-stats-row">
                        <Col span={8}>
                            <div className="stat-item">
                                <div className="stat-label">Completed</div>
                                <div className="stat-value">{completedAudit}</div>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="stat-item">
                                <div className="stat-label">Outstanding</div>
                                <div className="stat-value">{outstandingAudit}</div>
                            </div>
                        </Col>
                        <Col span={8}>
                            <div className="stat-item">
                                <div className="stat-label">Total Audit</div>
                                <div className="stat-value">{totalAudit}</div>
                            </div>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
};

export default CleaningAuditChart;